import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const MilestoneForm = ({ isOpen, onClose, onSave, milestone = null, property = null, loading = false }) => {
  const [formData, setFormData] = useState({
    category: 'AUTRE',
    name: '',
    due_date: '',
    amount: '',
    meta: {}
  });
  const [errors, setErrors] = useState({});

  const categories = [
    { value: 'HYPOTHEQUE', label: 'Hypothèque' },
    { value: 'ASSURANCE', label: 'Assurance' },
    { value: 'CHAUFFE_EAU', label: 'Chauffe-eau' },
    { value: 'CERTIFICAT_LOCALISATION', label: 'Certificat localisation' },
    { value: 'INSPECTION', label: 'Inspection' },
    { value: 'AUTRE', label: 'Autre' }
  ];

  useEffect(() => {
    if (milestone) {
      setFormData({
        category: milestone.category || 'AUTRE',
        name: milestone.name || '',
        due_date: milestone.due_date ? new Date(milestone.due_date).toISOString().split('T')[0] : '',
        amount: milestone.amount ? milestone.amount.toString() : '',
        meta: milestone.meta || {}
      });
    } else {
      setFormData({
        category: 'AUTRE',
        name: '',
        due_date: '',
        amount: '',
        meta: {}
      });
    }
    setErrors({});
  }, [milestone, isOpen]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleMetaChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      meta: { ...prev.meta, [field]: value }
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    }
    if (!formData.due_date) {
      newErrors.due_date = 'La date d\'échéance est requise';
    }
    if (formData.amount && isNaN(parseFloat(formData.amount))) {
      newErrors.amount = 'Le montant doit être un nombre valide';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const milestoneData = {
      category: formData.category,
      name: formData.name.trim(),
      due_date: formData.due_date,
      amount: formData.amount ? parseFloat(formData.amount) : null,
      meta: formData.meta,
      status: milestone?.status || 'UPCOMING'
    };

    await onSave(milestoneData);
  };

  const getCategoryFields = () => {
    switch (formData.category) {
      case 'HYPOTHEQUE':
        return (
          <>
            <Input
              label="Prêteur"
              value={formData.meta.lender || ''}
              onChange={(e) => handleMetaChange('lender', e.target.value)}
              placeholder="Ex: Banque Nationale"
            />
            <Input
              label="Taux d'intérêt (%)"
              value={formData.meta.rate || ''}
              onChange={(e) => handleMetaChange('rate', e.target.value)}
              placeholder="Ex: 5.5"
            />
            <Input
              label="Terme (mois)"
              value={formData.meta.term_months || ''}
              onChange={(e) => handleMetaChange('term_months', e.target.value)}
              placeholder="Ex: 300"
            />
            <Input
              label="Paiement mensuel ($)"
              value={formData.meta.payment || ''}
              onChange={(e) => handleMetaChange('payment', e.target.value)}
              placeholder="Ex: 1500"
            />
            <Input
              label="Solde actuel ($)"
              value={formData.meta.balance || ''}
              onChange={(e) => handleMetaChange('balance', e.target.value)}
              placeholder="Ex: 250000"
            />
            <div>
              <label style={{
                display: 'block',
                fontFamily: 'var(--font-body)',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'var(--color-foreground)',
                marginBottom: 'var(--spacing-xs)'
              }}>
                Notes
              </label>
              <textarea
                value={formData.meta.notes || ''}
                onChange={(e) => handleMetaChange('notes', e.target.value)}
                placeholder="Notes additionnelles..."
                rows={3}
                style={{
                  width: '100%',
                  padding: 'var(--spacing-sm)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-background)',
                  color: 'var(--color-foreground)',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  resize: 'vertical',
                  minHeight: '80px'
                }}
              />
            </div>
          </>
        );
      case 'ASSURANCE':
        return (
          <>
            <Input
              label="Assureur"
              value={formData.meta.insurer || ''}
              onChange={(e) => handleMetaChange('insurer', e.target.value)}
              placeholder="Ex: Intact Assurance"
            />
            <Input
              label="Numéro de police"
              value={formData.meta.policy_number || ''}
              onChange={(e) => handleMetaChange('policy_number', e.target.value)}
              placeholder="Ex: POL-123456"
            />
            <div>
              <label style={{
                display: 'block',
                fontFamily: 'var(--font-body)',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'var(--color-foreground)',
                marginBottom: 'var(--spacing-xs)'
              }}>
                Résumé de couverture
              </label>
              <textarea
                value={formData.meta.coverage_summary || ''}
                onChange={(e) => handleMetaChange('coverage_summary', e.target.value)}
                placeholder="Ex: Assurance habitation complète"
                rows={2}
                style={{
                  width: '100%',
                  padding: 'var(--spacing-sm)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-background)',
                  color: 'var(--color-foreground)',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  resize: 'vertical',
                  minHeight: '60px'
                }}
              />
            </div>
            <div>
              <label style={{
                display: 'block',
                fontFamily: 'var(--font-body)',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'var(--color-foreground)',
                marginBottom: 'var(--spacing-xs)'
              }}>
                Notes
              </label>
              <textarea
                value={formData.meta.notes || ''}
                onChange={(e) => handleMetaChange('notes', e.target.value)}
                placeholder="Notes additionnelles..."
                rows={3}
                style={{
                  width: '100%',
                  padding: 'var(--spacing-sm)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-background)',
                  color: 'var(--color-foreground)',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  resize: 'vertical',
                  minHeight: '80px'
                }}
              />
            </div>
          </>
        );
      case 'CHAUFFE_EAU':
        return (
          <>
            <Input
              label="Date d'installation"
              type="date"
              value={formData.meta.install_date ? new Date(formData.meta.install_date).toISOString().split('T')[0] : ''}
              onChange={(e) => handleMetaChange('install_date', e.target.value)}
            />
            <Input
              label="Durée de vie estimée (années)"
              value={formData.meta.expected_lifespan_years || '10'}
              onChange={(e) => {
                const years = parseInt(e.target.value) || 10;
                handleMetaChange('expected_lifespan_years', years);
                // Auto-calculate replacement date
                if (formData.meta.install_date) {
                  const installDate = new Date(formData.meta.install_date);
                  installDate.setFullYear(installDate.getFullYear() + years);
                  if (!formData.due_date || formData.due_date === '') {
                    setFormData(prev => ({
                      ...prev,
                      due_date: installDate.toISOString().split('T')[0]
                    }));
                  }
                }
              }}
              placeholder="10"
            />
            <Input
              label="Date du dernier entretien"
              type="date"
              value={formData.meta.last_service_date ? new Date(formData.meta.last_service_date).toISOString().split('T')[0] : ''}
              onChange={(e) => handleMetaChange('last_service_date', e.target.value)}
            />
            <div>
              <label style={{
                display: 'block',
                fontFamily: 'var(--font-body)',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'var(--color-foreground)',
                marginBottom: 'var(--spacing-xs)'
              }}>
                Notes
              </label>
              <textarea
                value={formData.meta.notes || ''}
                onChange={(e) => handleMetaChange('notes', e.target.value)}
                placeholder="Notes additionnelles..."
                rows={3}
                style={{
                  width: '100%',
                  padding: 'var(--spacing-sm)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-background)',
                  color: 'var(--color-foreground)',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  resize: 'vertical',
                  minHeight: '80px'
                }}
              />
            </div>
          </>
        );
      default:
        return (
            <div>
              <label style={{
                display: 'block',
                fontFamily: 'var(--font-body)',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'var(--color-foreground)',
                marginBottom: 'var(--spacing-xs)'
              }}>
                Notes
              </label>
              <textarea
                value={formData.meta.notes || ''}
                onChange={(e) => handleMetaChange('notes', e.target.value)}
                placeholder="Notes additionnelles..."
                rows={3}
                style={{
                  width: '100%',
                  padding: 'var(--spacing-sm)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-background)',
                  color: 'var(--color-foreground)',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  resize: 'vertical',
                  minHeight: '80px'
                }}
              />
            </div>
        );
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 'var(--spacing-md)'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--color-card)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--spacing-lg)',
          maxWidth: '600px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--spacing-lg)' }}>
          <h2 style={{
            fontFamily: 'var(--font-heading)',
            fontSize: '1.5rem',
            fontWeight: 600,
            color: 'var(--color-foreground)',
            margin: 0
          }}>
            {milestone ? 'Modifier l\'échéance' : 'Ajouter une échéance'}
          </h2>
          <Button
            variant="ghost"
            size="icon"
            iconName="X"
            onClick={onClose}
          />
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
            <Select
              label="Catégorie"
              value={formData.category}
              onChange={(e) => handleChange('category', e.target.value)}
              options={categories}
              required
            />

            <Input
              label="Nom de l'échéance"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Ex: Renouvellement hypothécaire"
              required
              error={errors.name}
            />

            <Input
              label="Date d'échéance"
              type="date"
              value={formData.due_date}
              onChange={(e) => handleChange('due_date', e.target.value)}
              required
              error={errors.due_date}
            />

            <Input
              label="Montant (optionnel)"
              type="number"
              value={formData.amount}
              onChange={(e) => handleChange('amount', e.target.value)}
              placeholder="Ex: 1200"
              error={errors.amount}
            />

            {getCategoryFields()}

            <div style={{ display: 'flex', gap: 'var(--spacing-sm)', justifyContent: 'flex-end', marginTop: 'var(--spacing-md)' }}>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                variant="default"
                iconName="Save"
                loading={loading}
              >
                {milestone ? 'Enregistrer' : 'Ajouter'}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MilestoneForm;
